import React from 'react';
import { User, Building2, Shield, Briefcase } from 'lucide-react';
import { useAgreement } from '../context/AgreementContext';
import { PurchaserType } from '../types';

interface PurchaserOption {
  type: PurchaserType;
  title: string;
  description: string;
  icon: React.ReactNode;
}

const purchaserOptions: PurchaserOption[] = [
  {
    type: 'personal',
    title: 'Personal',
    description: 'Individual or joint personal purchase',
    icon: <User className="w-6 h-6" />
  },
  {
    type: 'smsf',
    title: 'SMSF',
    description: 'Self-Managed Superannuation Fund',
    icon: <Shield className="w-6 h-6" />
  },
  {
    type: 'trust',
    title: 'Individual Trust',
    description: 'Individual trust structure',
    icon: <Building2 className="w-6 h-6" />
  },
  {
    type: 'company',
    title: 'Company Trust',
    description: 'Company trust structure',
    icon: <Briefcase className="w-6 h-6" />
  }
];

interface PurchaserTypeStepProps {
  onNext: () => void;
  agreementStatus?: string | null;
}

export function PurchaserTypeStep({ onNext, agreementStatus }: PurchaserTypeStepProps) {
  const { state, dispatch } = useAgreement();
  const { purchaserType } = state;
  
  // Disable all buttons when agreement is confirmed
  const isConfirmed = agreementStatus === 'confirmed';

  const handleSelection = (type: PurchaserType) => {
    // Don't update anything if agreement is confirmed
    if (isConfirmed) return;
    
    // Only update the purchaser type if it's different from the current selection
    // This prevents data loss when selecting the same type again
    if (type !== purchaserType) {
      console.log(`Changing purchaser type from ${purchaserType} to ${type}`);
      dispatch({ type: 'SET_PURCHASER_TYPE', payload: type });
    } else {
      console.log(`Selected the same purchaser type: ${type} - preserving existing data`);
      // No need to dispatch an action if the type hasn't changed
    }
  };

  const handleNext = () => {
    if (purchaserType) {
      onNext();
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6 h-fit">
      <div className="mb-6">
        <h2 className="text-xl font-bold text-gray-900 mb-2">
          What type of purchaser are you?
        </h2>
        <p className="text-sm text-gray-600">
          Select the entity type that will be purchasing the property.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3 mb-6">
        {purchaserOptions.map((option) => (
          <button
            key={option.type}
            onClick={() => !isConfirmed && handleSelection(option.type)}
            disabled={isConfirmed}
            className={`p-4 rounded-lg border-2 text-center transition-all duration-200 min-h-[100px] ${
              purchaserType === option.type
                ? 'border-primary-500 bg-primary-50'
                : 'border-gray-200 hover:border-gray-300'
            } ${isConfirmed ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <div className={`mb-2 flex justify-center ${
              purchaserType === option.type ? 'text-primary-600' : 'text-gray-600'
            }`}>
              {option.icon}
            </div>
            <h3 className="text-sm font-semibold text-gray-900 mb-1">
              {option.title}
            </h3>
            <p className="text-xs text-gray-600">
              {option.description}
            </p>
          </button>
        ))}
      </div>

      <div className="flex justify-end">
        <button
          onClick={handleNext}
          disabled={!purchaserType}
          className={`w-full sm:w-auto px-6 py-2.5 rounded-full font-semibold transition-all duration-200 min-h-[42px] ${
            purchaserType
              ? 'bg-primary-500 hover:bg-primary-600 text-white hover:shadow-md'
              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
          }`}
        >
          Continue
        </button>
      </div>
    </div>
  );
}